import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import DramaCard from '@/components/feature/DramaCard';
import { dramas, dramaCategories, hotDramas, chineseDramas, koreanDramas, latestDramas } from '@/mocks/dramas';

export default function HomePage() {
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);
  const heroDramas = hotDramas.slice(0, 3);

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '97影视',
    url: 'https://www.97yingshi.com',
    description: '97影视提供最全电视剧全集在线观看，覆盖最新热播剧、国产剧、韩剧，支持免费高清播放，更新快不卡顿。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.97yingshi.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '97影视',
    url: 'https://www.97yingshi.com',
    description: '97影视 - 最新热播电视剧全集在线观看平台',
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>

      <main>
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Epic%20cinematic%20composition%20with%20dramatic%20red%20and%20crimson%20curtain%20draped%20elegantly%20across%20a%20dark%20theater%20stage%2C%20warm%20spotlight%20creating%20atmospheric%20glow%2C%20abstract%20artistic%20background%20with%20film%20texture%20and%20golden%20particles%20floating%2C%20luxurious%20and%20mysterious%20mood%2C%20perfect%20for%20movie%20and%20drama%20platform%20hero%20background&width=1920&height=1080&seq=hero-bg-2026&orientation=landscape"
            alt="97影视 - 电视剧在线观看"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>

          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4 leading-tight">
                  追剧不等待，<span className="text-accent-500">精彩</span>即刻来
                </h1>
                <p className="text-base md:text-lg text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
                  国产剧、韩剧、美剧、日剧 — 海量电视剧全集高清在线观看，每日同步更新，满足你的所有追剧需求
                </p>
                <div className="flex items-center justify-center gap-3">
                  <Link
                    to="/category/hot"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-fire-line"></i>
                    热播推荐
                  </Link>
                  <Link
                    to="/category/all"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white/15 text-white text-sm font-medium hover:bg-white/25 backdrop-blur-sm transition-colors whitespace-nowrap border border-white/20"
                  >
                    <i className="ri-slideshow-3-line"></i>
                    全部剧集
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {heroDramas.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentHeroSlide(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentHeroSlide ? 'bg-accent-500 w-6' : 'bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`切换到第${i + 1}张`}
              />
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-2 py-4 overflow-x-auto scrollbar-hide">
              {dramaCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    cat.slug === 'hot'
                      ? 'bg-primary-500 text-white hover:bg-primary-600'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200 hover:text-primary-500'
                  }`}
                >
                  {cat.slug === 'hot' && <i className="ri-fire-line text-sm"></i>}
                  {cat.slug === 'chinese' && <i className="ri-flag-2-line text-sm"></i>}
                  {cat.slug === 'korean' && <i className="ri-kakao-talk-line text-sm"></i>}
                  {cat.slug === 'american' && <i className="ri-earth-line text-sm"></i>}
                  {cat.slug === 'japanese' && <i className="ri-sun-line text-sm"></i>}
                  {cat.slug === 'thai' && <i className="ri-palette-line text-sm"></i>}
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  <i className="ri-fire-line text-accent-500 mr-2"></i>
                  热播推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">大家都在追的热门好剧</p>
              </div>
              <Link
                to="/category/hot"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {hotDramas.slice(0, 6).map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          </div>
        </section>

        {/* Chinese Dramas */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  <i className="ri-flag-2-line text-primary-500 mr-2"></i>
                  国产剧精选
                </h2>
                <p className="text-sm text-foreground-500 mt-1">口碑国产好剧，值得一看</p>
              </div>
              <Link
                to="/category/chinese"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {chineseDramas.slice(0, 6).map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          </div>
        </section>

        {/* Korean Dramas */}
        <section className="bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  <i className="ri-heart-pulse-line text-accent-500 mr-2"></i>
                  韩剧专区
                </h2>
                <p className="text-sm text-foreground-500 mt-1">高分韩剧，部部经典</p>
              </div>
              <Link
                to="/category/korean"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {koreanDramas.slice(0, 6).map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  <i className="ri-timer-flash-line text-primary-500 mr-2"></i>
                  最新更新
                </h2>
                <p className="text-sm text-foreground-500 mt-1">热门剧集每日同步更新</p>
              </div>
              <Link
                to="/category/all"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {latestDramas.slice(0, 6).map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          </div>
        </section>

        {/* Features Bar */}
        <section className="bg-primary-500 py-10 md:py-12">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {[
                { icon: 'ri-hd-line', title: '高清画质', desc: '1080P超清体验' },
                { icon: 'ri-speed-up-line', title: '极速播放', desc: '不卡顿不缓冲' },
                { icon: 'ri-refresh-line', title: '每日更新', desc: '同步最新剧集' },
                { icon: 'ri-shield-check-line', title: '安全无广告', desc: '纯净追剧体验' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 flex-shrink-0">
                    <i className={`${item.icon} text-xl text-white`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{item.title}</p>
                    <p className="text-xs text-white/70">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}